/**
 * New Diagram Modal
 * Modal for creating a new diagram with proper validation and accessibility
 */

import { useState, memo, useCallback } from 'react'
import Modal from './Modal'
import DiagramTypeSelector, { DiagramTypeOption } from './DiagramTypeSelector'

export interface NewDiagramConfig {
  title: string
  diagramType: string
  description?: string
}

interface NewDiagramModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (config: NewDiagramConfig) => void
}

// Diagram type options - only Architecture is enabled for now
const DIAGRAM_TYPES: DiagramTypeOption[] = [
  {
    type: 'architecture',
    label: 'Architecture',
    icon: '🏗️',
    description: 'System structure, components, and relationships',
    enabled: true,
  },
  {
    type: 'user-flow',
    label: 'User Flow',
    icon: '👤',
    description: 'User journeys, screens, and actions',
    enabled: false,
    comingSoon: true,
  },
  {
    type: 'process-flow',
    label: 'Process Flow',
    icon: '⚙️',
    description: 'Business logic, workflows, and decisions',
    enabled: false,
    comingSoon: true,
  },
  {
    type: 'database-schema',
    label: 'Database Schema',
    icon: '🗄️',
    description: 'Data models, tables, and relationships',
    enabled: false,
    comingSoon: true,
  },
  {
    type: 'sequence',
    label: 'Sequence Diagram',
    icon: '📨',
    description: 'Time-based interactions between systems',
    enabled: false,
    comingSoon: true,
  },
]

// Form validation errors
interface FormErrors {
  title?: string
  description?: string
}

function NewDiagramModal({ isOpen, onClose, onCreate }: NewDiagramModalProps) {
  const [title, setTitle] = useState('')
  const [diagramType, setDiagramType] = useState('architecture')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Validate form
  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {}

    // Title validation
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      newErrors.title = 'Title is required'
    } else if (trimmedTitle.length < 1 || trimmedTitle.length > 100) {
      newErrors.title = 'Title must be 1-100 characters'
    }

    // Description validation
    if (description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters'
    }

    return newErrors
  }, [title, description])

  // Handle title change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value)
    if (touched.title) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        const trimmedTitle = e.target.value.trim()
        if (!trimmedTitle) {
          newErrors.title = 'Title is required'
        } else if (trimmedTitle.length < 1 || trimmedTitle.length > 100) {
          newErrors.title = 'Title must be 1-100 characters'
        } else {
          delete newErrors.title
        }
        return newErrors
      })
    }
  }

  // Handle description change
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value)
    if (touched.description) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        if (e.target.value.trim().length > 500) {
          newErrors.description = 'Description must be less than 500 characters'
        } else {
          delete newErrors.description
        }
        return newErrors
      })
    }
  }

  // Handle blur events
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const validationErrors = validateForm()
    setErrors(validationErrors)
  }

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Mark all fields as touched
    setTouched({ title: true, description: true })

    // Validate
    const validationErrors = validateForm()
    setErrors(validationErrors)

    // If there are errors, don't submit
    if (Object.keys(validationErrors).length > 0) {
      return
    }

    // Submit form
    onCreate({
      title: title.trim(),
      diagramType,
      description: description.trim() || undefined,
    })

    // Reset form
    handleReset()
  }

  // Reset form state
  const handleReset = () => {
    setTitle('')
    setDiagramType('architecture')
    setDescription('')
    setErrors({})
    setTouched({})
  }

  // Handle modal close
  const handleClose = () => {
    handleReset()
    onClose()
  }

  // Modal footer with buttons
  const footer = (
    <>
      <button type="button" className="btn-secondary" onClick={handleClose}>
        Cancel
      </button>
      <button type="submit" className="btn-primary" form="new-diagram-form">
        Create Diagram
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Diagram"
      footer={footer}
      maxWidth={600}
      ariaDescribedBy="new-diagram-description"
    >
      <form id="new-diagram-form" onSubmit={handleSubmit}>
        {/* Diagram Type */}
        <div className="form-group">
          <label id="diagram-type-label">
            Diagram Type <span className="required-asterisk">*</span>
          </label>
          <DiagramTypeSelector
            value={diagramType}
            onChange={setDiagramType}
            options={DIAGRAM_TYPES}
          />
        </div>

        {/* Title */}
        <div className="form-group">
          <label htmlFor="diagram-title">
            Title <span className="required-asterisk">*</span>
          </label>
          <input
            id="diagram-title"
            type="text"
            value={title}
            onChange={handleTitleChange}
            onBlur={() => handleBlur('title')}
            placeholder="e.g., E-Commerce System"
            aria-required="true"
            aria-invalid={!!errors.title}
            aria-describedby={errors.title ? 'title-error' : undefined}
            maxLength={100}
          />
          {errors.title && touched.title && (
            <div className="form-error" id="title-error" role="alert">
              {errors.title}
            </div>
          )}
        </div>

        {/* Description (optional) */}
        <div className="form-group">
          <label htmlFor="diagram-description">Description (optional)</label>
          <textarea
            id="diagram-description"
            value={description}
            onChange={handleDescriptionChange}
            onBlur={() => handleBlur('description')}
            placeholder="Brief description of your diagram..."
            rows={3}
            aria-invalid={!!errors.description}
            aria-describedby={errors.description ? 'description-error' : undefined}
            maxLength={500}
          />
          <div className="form-hint">
            {description.length}/500 characters
          </div>
          {errors.description && touched.description && (
            <div className="form-error" id="description-error" role="alert">
              {errors.description}
            </div>
          )}
        </div>
      </form>
    </Modal>
  )
}

export default memo(NewDiagramModal)
